/**
 * What a held calendar change does, in a shape a person can read at a glance:
 * which event, where it sits now, where it goes, who else is on it, and who
 * gets an email about it. The calendar tool writes it when it issues an
 * approval card; the Notifications
 * row draws it. Times stay epochs with the calendar's timezone, so the row
 * formats them in the reader's language.
 */

export type CalendarChangeKind = "move" | "edit" | "remove" | "add";

export type CalendarChangeSlot = {
  start: number;
  end?: number;
};

/**
 * One change that is neither time nor title. A location without a value clears
 * it; status and priority carry their new value.
 */
export type CalendarChangeDetail =
  | { field: "location"; value?: string }
  | { field: "notes" }
  | { field: "status"; value: string }
  | { field: "priority"; value: string };

export type CalendarChangeEntry = {
  kind: CalendarChangeKind;
  title: string;
  /** The new title, when the change renames the event. */
  renamedTo?: string;
  /** Where the event sits now. Absent for an event the change adds, or one without a time. */
  before?: CalendarChangeSlot;
  /** Where it sits after a move, or when an added event starts. */
  after?: CalendarChangeSlot;
  allDay?: boolean;
  /** Changes that are neither time nor title. */
  details: CalendarChangeDetail[];
  /** Other people on the event, by name or email. */
  people: string[];
  /** Other people beyond the ones named. */
  morePeople: number;
  /**
   * The people approving this change emails about it, by name or email. Present
   * only when the approval sends that email, so the card always says it does.
   */
  notify?: CalendarChangeNotice;
};

export type CalendarChangeNotice = {
  people: string[];
  /** People emailed beyond the ones named. */
  more: number;
};

export type CalendarChangeReview = {
  /** The approval this review describes; a review for another approval is never shown. */
  previewId: string;
  timezone: string;
  /** How far a recurring change reaches. */
  reach?: "series" | "following";
  entries: CalendarChangeEntry[];
  /** Changes in the batch beyond the entries shown. */
  more: number;
  /**
   * Why the change is offered, when Smart Manager offers it unasked: one
   * sentence the card and the chat lead with ("Rain is expected during …").
   * Written from our own data only, never rewritten afterwards.
   */
  lead?: string;
};

export const CALENDAR_REVIEW_ENTRY_LIMIT = 10;
export const CALENDAR_REVIEW_PEOPLE_LIMIT = 6;
const TEXT_LIMIT = 200;
const LEAD_LIMIT = 400;
const DETAIL_LIMIT = 4;

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim().slice(0, TEXT_LIMIT) : null;
const epoch = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const count = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;

const KINDS: ReadonlySet<string> = new Set<CalendarChangeKind>(["move", "edit", "remove", "add"]);

function readSlot(value: unknown): CalendarChangeSlot | null {
  const slot = record(value);
  const start = epoch(slot?.start);
  if (!slot || start === null) return null;
  const end = epoch(slot.end);
  return end !== null && end > start ? { start, end } : { start };
}

function readStrings(value: unknown, limit: number): string[] | null {
  if (!Array.isArray(value)) return null;
  const strings = value.map(text);
  return strings.every((entry): entry is string => entry !== null) ? strings.slice(0, limit) : null;
}

function readDetail(value: unknown): CalendarChangeDetail | null {
  const detail = record(value);
  if (!detail) return null;
  const detailValue = text(detail.value);
  switch (detail.field) {
    case "location":
      return detailValue ? { field: "location", value: detailValue } : { field: "location" };
    case "notes":
      return { field: "notes" };
    case "status":
    case "priority":
      return detailValue ? { field: detail.field, value: detailValue } : null;
    default:
      return null;
  }
}

function readDetails(value: unknown): CalendarChangeDetail[] | null {
  if (!Array.isArray(value)) return null;
  const details = value.map(readDetail);
  return details.every((detail): detail is CalendarChangeDetail => detail !== null) ? details.slice(0, DETAIL_LIMIT) : null;
}

/** Undefined when absent; null when present but malformed, which rejects the entry. */
function readNotice(value: unknown): CalendarChangeNotice | null | undefined {
  if (value === undefined) return undefined;
  const notice = record(value);
  const people = readStrings(notice?.people, CALENDAR_REVIEW_PEOPLE_LIMIT);
  const more = count(notice?.more);
  if (!people || more === null || people.length === 0) return null;
  return { people, more };
}

function readEntry(value: unknown): CalendarChangeEntry | null {
  const entry = record(value);
  const title = text(entry?.title);
  if (!entry || !title || typeof entry.kind !== "string" || !KINDS.has(entry.kind)) return null;
  const kind = entry.kind as CalendarChangeKind;
  const before = entry.before === undefined ? undefined : readSlot(entry.before);
  const after = entry.after === undefined ? undefined : readSlot(entry.after);
  if (before === null || after === null) return null;
  if (kind === "move" && (!before || !after)) return null;
  const people = readStrings(entry.people, CALENDAR_REVIEW_PEOPLE_LIMIT);
  const details = readDetails(entry.details);
  const morePeople = count(entry.morePeople);
  const notify = readNotice(entry.notify);
  if (!people || !details || morePeople === null || notify === null) return null;
  // Only a change to an existing event emails the people on it.
  if (notify && kind !== "move" && kind !== "edit") return null;
  const renamedTo = text(entry.renamedTo);
  return {
    kind,
    title,
    ...(renamedTo ? { renamedTo } : {}),
    ...(before ? { before } : {}),
    ...(after ? { after } : {}),
    ...(entry.allDay === true ? { allDay: true } : {}),
    details,
    people,
    morePeople,
    ...(notify ? { notify } : {}),
  };
}

/** The review, or null when the value is not a well-formed one. */
export function readCalendarChangeReview(value: unknown): CalendarChangeReview | null {
  const review = record(value);
  const previewId = text(review?.previewId);
  const timezone = text(review?.timezone);
  if (!review || !previewId || !timezone || !Array.isArray(review.entries)) return null;
  const entries = review.entries.slice(0, CALENDAR_REVIEW_ENTRY_LIMIT).map(readEntry);
  const more = count(review.more);
  if (entries.length === 0 || more === null || !entries.every((entry): entry is CalendarChangeEntry => entry !== null)) {
    return null;
  }
  const reach = review.reach === "series" || review.reach === "following" ? review.reach : undefined;
  const lead = typeof review.lead === "string" && review.lead.trim() ? review.lead.trim().slice(0, LEAD_LIMIT) : undefined;
  return { previewId, timezone, ...(reach ? { reach } : {}), entries, more, ...(lead ? { lead } : {}) };
}
